import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Box,
  CircularProgress,
  Alert,
  Icon,
  Button,
  Chip,
  useTheme,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Build,
  Visibility
} from '@mui/icons-material';
import { getAllServices, resolveImageUrl } from '../utils/api';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[10],
  },
}));

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
  color: 'white',
  overflow: 'hidden',
  marginTop: '-90px',
  paddingTop: '90px',
  [theme.breakpoints.down('md')]: {
    minHeight: '80vh',
    paddingTop: '70px',
    marginTop: '-70px',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '60vh',
    paddingTop: '56px',
    marginTop: '-56px',
  },
}));

const SliderContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
});

const SlideImage = styled(motion.div)(({ isActive }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  opacity: isActive ? 1 : 0,
  zIndex: 1,
  filter: 'brightness(1.1)',
  transition: 'opacity 0.6s cubic-bezier(0.4,0,0.2,1)',
  pointerEvents: 'none',
}));

const SliderOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.2)',
  zIndex: 2,
});

const SliderDots = styled(Box)({
  position: 'absolute',
  bottom: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '0.5rem',
  zIndex: 4,
});

const SliderDot = styled(Box)(({ isActive }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: isActive ? 'white' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'white',
    transform: 'scale(1.2)',
  },
}));

const SlideContent = styled(motion.div)(({ isActive, theme }) => ({
  opacity: isActive ? 1 : 0,
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'left',
  pointerEvents: isActive ? 'auto' : 'none',
  zIndex: 3,
  paddingLeft: '6vw',
  color: 'white',
  [theme.breakpoints.down('md')]: {
    paddingLeft: '3vw',
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '2vw',
    paddingTop: '4vh'
  },
}));

const Services = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchServices();
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (!services || services.length === 0) return;
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % Math.min(4, services.length));
    }, 4000);
    return () => clearTimeout(timer);
  }, [currentSlide, services]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await getAllServices();

      // Handle the actual API response structure
      const servicesData = response?.data?.services || response?.services || [];
      setServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (err) {
      setError('Failed to load services. Please try again later.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedService(null);
  };

  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading services...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Use static images for slider (renamed)
  const sliderImages = [
    '/servicesSlider/1.jpg',
    '/servicesSlider/2.jpg',
    '/servicesSlider/3.jpg',
    '/servicesSlider/4.jpg',
  ];

  // Sort services by id (latest first)
  const sortedServices = [...services].sort((a, b) => (b.id || 0) - (a.id || 0));
  // Use first 4 services for slider content
  const sliderServices = sortedServices.slice(0, 4);

  return (
    <>
      {/* Services Hero Section with Slider and Header */}
      <HeroSection>
        <SliderContainer>
          {sliderImages.map((img, idx) => (
            <SlideImage
              key={img}
              isActive={currentSlide === idx}
              style={{ backgroundImage: `url(${img})` }}
              animate={{ opacity: currentSlide === idx ? 1 : 0 }}
              transition={{ duration: 0.6 }}
            >
            </SlideImage>
          ))}
          <SliderOverlay />
          {sliderServices.map((service, idx) => (
            <SlideContent key={service.id + '-content'} isActive={currentSlide === idx}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.7rem', md: '2.2rem', lg: '2.7rem' },
                    lineHeight: 1.1,
                    maxWidth: { xs: '90vw', sm: '70vw', md: '500px' },
                  }}
                >
                  {/* First two words */}
                  {service.title.split(' ').slice(0, 2).join(' ')}{' '}
                  {/* Remaining words with color, only if present */}
                  {service.title.split(' ').length > 2 && (
                    <span style={{ color: theme ? theme.palette.secondary.main : '#1976d2' }}>
                      {service.title.split(' ').slice(2).join(' ')}
                    </span>
                  )}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '0.8rem', md: '1.1rem' },
                    fontWeight: 400,
                    opacity: 1,
                    maxWidth: { xs: '300px', sm: '400px', md: '500px' },
                    lineHeight: 1.6,
                  }}
                >
                  {service.description?.slice(0, 120)}{service.description?.length > 120 ? '...' : ''}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<Visibility />}
                  onClick={() => navigate(`/services/${service.slug || service.id}`)}
                  sx={{
                    mt: 2,
                    py: 1,
                    px: 2.5,
                    fontSize: { xs: '0.5rem', sm: '0.7rem', md: '0.8rem', lg: '0.85rem', xl: '0.95rem' },
                    borderRadius: '50px',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    fontWeight: 600,
                    background: 'rgba(255,255,255,0.04)',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  View Service
                </Button>
              </Box>
            </SlideContent>
          ))}
          <SliderDots>
            {sliderImages.map((_, idx) => (
              <SliderDot
                key={idx}
                isActive={currentSlide === idx}
                onClick={() => setCurrentSlide(idx)}
              />
            ))}
          </SliderDots>
        </SliderContainer>
      </HeroSection>

      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mt: { xs: 2, md: 4 }, width: '100%' }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              color: '#333333',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
            }}
          >
            Our Services
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: '600px', mx: 'auto', textShadow: '0 2px 8px rgba(0,0,0,0.04)', fontSize: { xs: '0.8rem', sm: '1rem', md: '1.2rem' } }}
          >
            We offer comprehensive construction services to meet all your building needs.
            From design to completion, we handle every aspect of your project with precision and care.
          </Typography>
        </Box>

        {/* Services Grid */}
        {services.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No services available at the moment.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4} sx={{ py: 5}}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={service.id || index}>
                <StyledCard>
                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    {/* Icon and Title on same line */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {service.icon_url ? (
                          <Avatar
                            src={resolveImageUrl(service.icon_url)}
                            sx={{
                              width: 60,
                              height: 60,
                              background: 'transparent',
                            }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 60,
                              height: 60,
                              background: '#DDE6ED',
                              color: '#2C3E50',
                            }}
                          >
                            <Build sx={{ fontSize: 30 }} />
                          </Avatar>
                        )}
                      </Box>
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 600, flex: 1, color: '#2C3E50' }}>
                        {service.title}
                      </Typography>
                    </Box>

                    {/* Features */}
                    {service.features && service.features.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {service.features.slice(0, 3).map((feature, featureIndex) => (
                            <Chip
                              key={featureIndex}
                              label={feature}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{
                                borderRadius: 3,
                                fontSize: '0.7rem',
                                height: 24
                              }}
                            />
                          ))}
                          {service.features.length > 3 && (
                            <Chip
                              label={`+${service.features.length - 3} more`}
                              size="small"
                              variant="filled"
                              color="secondary"
                              sx={{
                                borderRadius: 3,
                                fontSize: '0.7rem',
                                height: 24
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Description - Truncated to 3 lines */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.7,
                      }}
                    >
                      {service.description.slice(0, 150) + "..."}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/services/${service.slug || service.id}`)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        color: '#2C3E50',
                        borderColor: '#2C3E50',
                        '&:hover': {
                          backgroundColor: '#2C3E50',
                          color: 'white',
                        },
                      }}
                    >
                      View Details
                    </Button>
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Call to Action Section */}
        <Box sx={{ textAlign: 'center', mt: 4, p: 4, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Ready to Start Your Project?
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Contact us today for a free consultation and quote
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              📞 +44 7931 937835 | ☎️ 020 35356266
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              ✉️ info@allinoneconstructions.com
            </Typography>
          </Box>
        </Box>

        {/* Dialog for linked projects */}
        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>
            Projects linked to: {selectedService?.title}
          </DialogTitle>
          <DialogContent>
            {selectedService?.projects && selectedService.projects.length > 0 ? (
              <Grid container spacing={2}>
                {selectedService.projects.map(project => (
                  <Grid item xs={12} sm={6} md={4} key={project.id}>
                    <Card sx={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${project.slug || project.id}`)}>
                      <CardContent>
                        <Typography variant="h6">{project.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {project.description?.slice(0, 60)}{project.description?.length > 60 ? '...' : ''}
                        </Typography>
                        <Chip label={project.status} size="small" sx={{ mr: 1 }} />
                        {project.category && <Chip label={project.category} size="small" />}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No projects linked to this service.
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
};

export default Services;